import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = `${BACKEND_URL.replace(/^http/, 'ws')}/api/ws`;

const http = axios.create({ baseURL: API, timeout: 15000 });

// ---- Session token (bearer): native WebViews cannot rely on cross-site cookies ----
export const TOKEN_KEY = 'd7_auth';
export const readSession = () => { try { return JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null'); } catch { return null; } };
export const writeSession = (session) => { if (session) localStorage.setItem(TOKEN_KEY, JSON.stringify(session)); else localStorage.removeItem(TOKEN_KEY); };

http.interceptors.request.use((config) => {
  const session = readSession();
  if (session?.token) config.headers.Authorization = `Bearer ${session.token}`;
  return config;
});

export const errorMessage = (e, fallback = 'Ocurrió un error') => {
  const detail = e?.response?.data?.detail;
  return Array.isArray(detail) ? (detail.find((item) => item.msg?.startsWith('Value error,'))?.msg.replace('Value error, ', '') || 'Revisa los datos ingresados: hay campos incompletos o inválidos.') : detail || e?.message || fallback;
};

const api = {
  // auth
  login: (role, identifier, password) => http.post('/auth/login', { role, identifier, password }).then((r) => r.data),
  authMe: () => http.get('/auth/me').then((r) => r.data),
  registerCustomer: (d) => http.post('/auth/register/customer', d).then((r) => r.data),
  googleLogin: (code, redirectUri) => http.post('/auth/google', { code, redirectUri }).then((r) => r.data),
  changePassword: (currentPassword, newPassword) => http.post('/auth/change-password', { currentPassword, newPassword }).then((r) => r.data),
  requestPasswordReset: (role, identifier) => http.post('/auth/forgot-password', { role, identifier }).then((r) => r.data),
  resetRequests: () => http.get('/auth/reset-requests').then((r) => r.data),
  resolveResetRequest: (id) => http.delete(`/auth/reset-requests/${id}`).then((r) => r.data),
  adminSetPassword: (role, userId, newPassword) => http.post('/auth/admin/set-password', { role, userId, newPassword }).then((r) => r.data),
  adminDisableAccount: (role, userId, disabled) => http.post('/auth/admin/disable', { role, userId, disabled }).then((r) => r.data),
  deleteMyAccount: (password, confirm) => http.post('/auth/delete-account', { password, confirm }).then((r) => r.data),
  adminDeleteCustomer: (userId) => http.post('/auth/admin/delete-customer', { userId }).then((r) => r.data),

  // collections
  restaurants: () => http.get('/restaurants').then((r) => r.data),
  menus: () => http.get('/menus').then((r) => r.data),
  drivers: () => http.get('/drivers').then((r) => r.data),
  customers: () => http.get('/customers').then((r) => r.data),
  orders: (params) => http.get('/orders', { params }).then((r) => r.data),
  banks: () => http.get('/banks').then((r) => r.data),
  notifications: () => http.get('/notifications').then((r) => r.data),
  stats: () => http.get('/stats/dashboard').then((r) => r.data),

  // restaurants
  createRestaurant: (d) => http.post('/restaurants', d).then((r) => r.data),
  updateRestaurant: (id, d) => http.put(`/restaurants/${id}`, d).then((r) => r.data),
  addBusinessAccount: (rid, data) => http.post(`/restaurants/${rid}/payment-accounts`, data).then((r) => r.data),
  editBusinessAccount: (rid, aid, data) => http.patch(`/restaurants/${rid}/payment-accounts/${aid}`, data).then((r) => r.data),
  deleteBusinessAccount: (rid, aid) => http.delete(`/restaurants/${rid}/payment-accounts/${aid}`).then((r) => r.data),
  deleteRestaurant: (id) => http.delete(`/restaurants/${id}`).then((r) => r.data),
  restaurantLogin: (email) => http.post('/restaurants/login', { email }).then((r) => r.data),
  registerRestaurant: (d) => http.post('/restaurants/register', d).then((r) => r.data),
  saveMenu: (rid, menu) => http.put(`/menus/${rid}`, menu).then((r) => r.data),

  // drivers
  createDriver: (d) => http.post('/drivers', d).then((r) => r.data),
  registerDriver: (d) => http.post('/drivers/register', d).then((r) => r.data),
  driverLogin: (phone) => http.post('/drivers/login', { phone }).then((r) => r.data),
  updateDriver: (id, d) => http.put(`/drivers/${id}`, d).then((r) => r.data),
  driverLocation: (id, d) => http.post(`/drivers/${id}/location`, d).then((r) => r.data),
  deleteDriver: (id) => http.delete(`/drivers/${id}`).then((r) => r.data),

  // customers
  customerLogin: (d) => http.post('/customers/login', d).then((r) => r.data),
  updateCustomer: (id, d) => http.put(`/customers/${id}`, d).then((r) => r.data),

  // orders
  createOrder: (d) => http.post('/orders', d).then((r) => r.data),
  updateOrder: (id, d) => http.put(`/orders/${id}`, d).then((r) => r.data),
  acceptOrder: (id, driverId) => http.post(`/orders/${id}/accept`, { driverId }).then((r) => r.data),
  deleteOrder: (id, reason) => http.delete(`/orders/${id}`, { params: { reason: reason || '' } }).then((r) => r.data),
  deletedOrders: () => http.get('/deleted-orders').then((r) => r.data),
  driverStats: (date) => http.get('/driver-stats', { params: date ? { date } : {} }).then((r) => r.data),
  driverHistory: (params) => http.get('/driver-stats/orders', { params }).then((r) => r.data),
  driverPay: () => http.get('/driver-pay').then((r) => r.data),
  saveDriverPay: (d) => http.put('/driver-pay', d).then((r) => r.data),
  autoAssign: () => http.get('/auto-assign').then((r) => r.data),
  saveAutoAssign: (d) => http.put('/auto-assign', d).then((r) => r.data),
  setDriverFee: (id, fee) => http.post(`/orders/${id}/driver-fee`, { fee }).then((r) => r.data),
  rejectOrder: (id, driverId, reason) => http.post(`/orders/${id}/reject`, { driverId, reason: reason || '' }).then((r) => r.data),
  decideReceipt: (orderId, status) => http.put(`/receipts/${orderId}`, { status }).then((r) => r.data),

  // banks
  createBank: (d) => http.post('/banks', d).then((r) => r.data),
  updateBank: (id, d) => http.put(`/banks/${id}`, d).then((r) => r.data),
  deleteBank: (id) => http.delete(`/banks/${id}`).then((r) => r.data),

  // notifications
  readAll: () => http.put('/notifications/read-all').then((r) => r.data),
  readOne: (id) => http.put(`/notifications/${id}/read`).then((r) => r.data),
  clearNotifications: () => http.delete('/notifications').then((r) => r.data),

  reset: () => http.post('/admin/reset').then((r) => r.data),
  registerDevice: (d) => http.post('/devices', d).then((r) => r.data),
  removeDevice: (token) => http.delete(`/devices/${token}`).then((r) => r.data),

  // ads (admin promo videos)
  ads: () => http.get('/ads').then((r) => r.data),
  createAd: (d) => http.post('/ads', d).then((r) => r.data),
  updateAd: (id, d) => http.put(`/ads/${id}`, d).then((r) => r.data),
  deleteAd: (id) => http.delete(`/ads/${id}`).then((r) => r.data),

  // coverage (city / barrio zones)
  deliveryPricing: () => http.get('/delivery-pricing').then((r) => r.data),
  packagePricing: () => http.get('/package-pricing').then((r) => r.data),
  savePackagePricing: (d) => http.put('/package-pricing', d).then((r) => r.data),
  packageQuote: (d) => http.post('/package-quote', d).then((r) => r.data),
  driverCall: (id, driverId) => http.post(`/orders/${id}/driver-call`, { driverId }).then((r) => r.data),
  businessReview: (id, data) => http.post(`/orders/${id}/business-review`, data).then((r) => r.data),
  chat: (id, actorRole, actorId) => http.get(`/orders/${id}/chat`, { params: { actorRole, actorId } }).then((r) => r.data),
  sendChat: (id, data) => http.post(`/orders/${id}/chat`, data).then((r) => r.data),
  readChat: (id, actorRole, actorId) => http.put(`/orders/${id}/chat/read`, { actorRole, actorId }).then((r) => r.data),
  unreadChat: (actorRole, actorId) => http.get('/chat/unread', { params: { actorRole, actorId } }).then((r) => r.data),
  uploadEvidence: (id, driverId, file) => { const form = new FormData(); form.append('driverId', driverId); form.append('file', file); return http.post(`/orders/${id}/delivery-evidence`, form, { timeout: 120000 }).then((r) => r.data); },
  saveDeliveryPricing: (d) => http.put('/delivery-pricing', d).then((r) => r.data),
  coverage: () => http.get('/coverage').then((r) => r.data),
  createCoverage: (d) => http.post('/coverage', d).then((r) => r.data),
  updateCoverage: (id, d) => http.put(`/coverage/${id}`, d).then((r) => r.data),
  deleteCoverage: (id) => http.delete(`/coverage/${id}`).then((r) => r.data),

  // driver messages (admin -> driver)
  driverMessages: (id) => http.get(`/drivers/${id}/messages`).then((r) => r.data),
  sendDriverMessage: (id, text) => http.post(`/drivers/${id}/message`, { text }).then((r) => r.data),
  readDriverMessages: (id) => http.put(`/drivers/${id}/messages/read-all`).then((r) => r.data),

  // driver notifications (admin broadcast to all / selected drivers)
  driverNotifications: () => http.get('/driver-notifications').then((r) => r.data),
  createDriverNotification: (d) => http.post('/driver-notifications', d).then((r) => r.data),
  deleteDriverNotification: (id) => http.delete(`/driver-notifications/${id}`).then((r) => r.data),

  // customer notification inbox
  customerNotifications: (customerId) => http.get('/customer-notifications', { params: { customerId } }).then((r) => r.data),
  readAllCustomerNotifications: (customerId) => http.put('/customer-notifications/read-all', { customerId }).then((r) => r.data),
  readCustomerNotification: (id) => http.put(`/customer-notifications/${id}/read`).then((r) => r.data),
  deleteCustomerNotification: (id) => http.delete(`/customer-notifications/${id}`).then((r) => r.data),
  clearCustomerNotifications: (customerId) => http.delete('/customer-notifications', { params: { customerId } }).then((r) => r.data),

  // customer promo campaigns (admin broadcast)
  customerCampaigns: () => http.get('/customer-campaigns').then((r) => r.data),
  createCustomerCampaign: (d) => http.post('/customer-campaigns', d).then((r) => r.data),
  deleteCustomerCampaign: (id) => http.delete(`/customer-campaigns/${id}`).then((r) => r.data),

  // mandadito price approval
  proposePrice: (id, fee) => http.post(`/orders/${id}/propose-price`, { fee }).then((r) => r.data),
  respondPrice: (id, accept) => http.post(`/orders/${id}/respond-price`, { accept }).then((r) => r.data),

  // business order stats (pickup vs delivery, monthly)
  businessStats: (month) => http.get('/business-stats', { params: month ? { month } : {} }).then((r) => r.data),
  businessStatsMonths: () => http.get('/business-stats/months').then((r) => r.data),
  resetBusinessStats: () => http.post('/business-stats/reset').then((r) => r.data),
  businessStatsPdfUrl: (month) => `${API}/business-stats/pdf${month ? `?month=${month}` : ''}`,
};

export default api;
